export type OutputFeedbackValue = "positive" | "negative";

export type OutputFeedbackOutputType = "chat_response" | "chat_artifact" | "task_output";

export type OutputFeedbackSurface = "chat" | "tasks";

export type OutputFeedbackArtifactKind = "pptx" | "excel";

interface OutputFeedbackTargetBase {
  outputId: string;
  surface?: OutputFeedbackSurface;
  sessionId?: string;
}

/**
 * Identity plus persistence context for `/api/v4/feedback`.
 *
 * Allowed fields follow Finster Chat's call sites and the upstream schema:
 * - `chat_response` rates a query. Requires `queryId` (Chat uses the same id
 *   for `outputId` and `queryId`). Does not accept `artifactKind`.
 * - `chat_artifact` rates a generated deck or workbook. Requires `artifactKind`.
 * - `task_output` rates a task run. May include `artifactKind` and task ids.
 */
export type OutputFeedbackTarget =
  | (OutputFeedbackTargetBase & {
      outputType: "chat_response";
      queryId: string;
    })
  | (OutputFeedbackTargetBase & {
      outputType: "chat_artifact";
      artifactKind: OutputFeedbackArtifactKind;
      queryId?: string;
      artifactVersion?: string;
    })
  | (OutputFeedbackTargetBase & {
      outputType: "task_output";
      artifactKind?: OutputFeedbackArtifactKind;
      taskId?: string;
      taskRunId?: string;
    });

/** A stored rating, as returned by the feedback API. */
export interface OutputFeedbackRating {
  outputId: string;
  outputType: OutputFeedbackOutputType;
  value: OutputFeedbackValue;
  reasons: string[];
}

export interface FeedbackReasonOption {
  id: string;
  label: string;
}

/** Amplitude context derived from a feedback target. Omits API-only fields. */
export function feedbackAnalyticsProperties(target: OutputFeedbackTarget): {
  outputId: string;
  outputType: OutputFeedbackOutputType;
  sessionId?: string;
  queryId?: string;
  artifactKind?: OutputFeedbackArtifactKind;
} {
  return {
    outputId: target.outputId,
    outputType: target.outputType,
    sessionId: target.sessionId,
    ...(target.outputType !== "task_output" && target.queryId
      ? { queryId: target.queryId }
      : {}),
    ...(target.outputType !== "chat_response" && target.artifactKind
      ? { artifactKind: target.artifactKind }
      : {}),
  };
}
